import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import backendService from '../services/backendService';
import { Game } from '../services/backendService/types';
import { gameCacheKey } from './useGame';

export const gamePinCacheKey = 'game-pin';

function useGamePin(gameId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = [gamePinCacheKey, gameId];

  // PIN Query
  const { data: pinData, isFetching, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!gameId) return { pin: null };
      // This is a small hack to avoid generating a new pin when we end the game.
      // We could consider moving this logic to the backend.
      const game = queryClient.getQueryData<Game | undefined>([gameCacheKey, gameId]);
      if (game?.isComplete) return { pin: null };
      return backendService.instance.game.pin.request(gameId, false);
    },
    enabled: Boolean(gameId),
    staleTime: 1000 * 60,
    retry: 3,
    retryDelay: 1000,
  });

  // Helper to update caches after mutation
  const updateCaches = (pin: string | null) => {
    queryClient.setQueryData(queryKey, { pin });
    queryClient.invalidateQueries({
      queryKey: queryKey
    });
  };

  // Request PIN (assign or get existing)
  const requestMutation = useMutation({
    mutationFn: () => backendService.instance.game.pin.request(gameId!, false),
    onSuccess: result => updateCaches(result.pin),
  });

  // Regenerate PIN (force new)
  const regenerateMutation = useMutation({
    mutationFn: () => backendService.instance.game.pin.request(gameId!, true),
    onSuccess: result => updateCaches(result.pin),
  });

  // Revoke PIN
  const revokeMutation = useMutation({
    mutationFn: () => backendService.instance.game.pin.revoke(gameId!),
    onSuccess: () => updateCaches(null),
  });

  return {
    pin: pinData?.pin,
    isFetching,
    isLoading,
    isError,
    refetch,
    requestPin: requestMutation.mutate,
    regeneratePin: regenerateMutation.mutate,
    revokePin: revokeMutation.mutate,
    isRequesting: requestMutation.isPending,
    isRegenerating: regenerateMutation.isPending,
    isRevoking: revokeMutation.isPending,
  };
}

export default useGamePin;
